import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useCallback, useRef, useState } from "react";
import {
    Animated,
    Dimensions,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Snackbar, Text } from "react-native-paper";

import { DiagnosisCard } from "./DiagnosisCard";
import type { DiagnosisClassification, DiagnosisSource, HubDiagnosis } from "./types";

import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

const { height: SCREEN_HEIGHT } = Dimensions.get("window");

// ── Props ──

interface Props {
    visible: boolean;
    diagnoses: HubDiagnosis[];
    onClose: () => void;
    onDeleteDiagnosis: (icdCode: string, source: DiagnosisSource) => void;
    onChangeClassification: (icdCode: string, newClassification: DiagnosisClassification) => void;
    onNavigateToSection: (sectionName: string) => void;
    onNavigateToMedications: () => void;
}

// ── Component ──

export function DiagnosisHubSheet({
    visible,
    diagnoses,
    onClose,
    onDeleteDiagnosis,
    onChangeClassification,
    onNavigateToSection,
    onNavigateToMedications
}: Props) {
    const slideAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
    const backdropAnim = useRef(new Animated.Value(0)).current;

    // Context menu state
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuTarget, setMenuTarget] = useState<{
        dx: HubDiagnosis;
        source: DiagnosisSource;
    } | null>(null);

    // Classification picker state
    const [pickerVisible, setPickerVisible] = useState(false);
    const [pickerTarget, setPickerTarget] = useState<HubDiagnosis | null>(null);

    // Undo snackbar
    const [undoVisible, setUndoVisible] = useState(false);
    const [_undoPending, setUndoPending] = useState<{
        icdCode: string;
        source: DiagnosisSource;
    } | null>(null);

    // Delete confirmation — separate target so it survives menu close
    const [deleteTarget, setDeleteTarget] = useState<{
        dx: HubDiagnosis;
        source: DiagnosisSource;
    } | null>(null);

    // Toast for classification change
    const [toastText, setToastText] = useState<string | null>(null);

    // ── Animations ──

    const animateIn = useCallback(() => {
        Animated.parallel([
            Animated.timing(backdropAnim, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true
            }),
            Animated.spring(slideAnim, {
                toValue: 0,
                friction: 10,
                tension: 65,
                useNativeDriver: true
            })
        ]).start();
    }, [backdropAnim, slideAnim]);

    const animateOut = useCallback(
        (cb?: () => void) => {
            Animated.parallel([
                Animated.timing(backdropAnim, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true
                }),
                Animated.timing(slideAnim, {
                    toValue: SCREEN_HEIGHT,
                    duration: 250,
                    useNativeDriver: true
                })
            ]).start(() => cb?.());
        },
        [backdropAnim, slideAnim]
    );

    const handleClose = useCallback(() => {
        setMenuVisible(false);
        setMenuTarget(null);
        setDeleteTarget(null);
        setPickerVisible(false);
        setPickerTarget(null);
        animateOut(onClose);
    }, [animateOut, onClose]);

    // ── Context Menu ──

    const openMenu = useCallback((dx: HubDiagnosis, source: DiagnosisSource) => {
        setMenuTarget({ dx, source });
        setMenuVisible(true);
    }, []);

    const closeMenu = useCallback(() => {
        setMenuVisible(false);
        setMenuTarget(null);
    }, []);

    const handleMenuNavigate = useCallback(() => {
        if (!menuTarget) {
            return;
        }
        closeMenu();
        animateOut(() => {
            if (menuTarget.source.type === "med") {
                onNavigateToMedications();
            } else if (menuTarget.source.type === "section") {
                onNavigateToSection(menuTarget.source.label);
            }
        });
    }, [menuTarget, closeMenu, animateOut, onNavigateToSection, onNavigateToMedications]);

    const handleMenuRemove = useCallback(() => {
        if (!menuTarget) {
            return;
        }
        // Copy target BEFORE closing menu (which nulls menuTarget)
        setDeleteTarget({ dx: menuTarget.dx, source: menuTarget.source });
        closeMenu();
    }, [menuTarget, closeMenu]);

    const confirmDeleteAction = useCallback(() => {
        if (!deleteTarget) {
            return;
        }
        setUndoPending({
            icdCode: deleteTarget.dx.icdCode,
            source: deleteTarget.source
        });
        onDeleteDiagnosis(deleteTarget.dx.icdCode, deleteTarget.source);
        setDeleteTarget(null);
        setUndoVisible(true);
    }, [deleteTarget, onDeleteDiagnosis]);

    const handleMenuClassify = useCallback(() => {
        if (!menuTarget) {
            return;
        }
        closeMenu();
        setPickerTarget(menuTarget.dx);
        setPickerVisible(true);
    }, [menuTarget, closeMenu]);

    // ── Classification Picker ──

    const handleClassificationChange = useCallback(
        (cls: DiagnosisClassification) => {
            if (!pickerTarget) {
                return;
            }
            // Check if another diagnosis is already principal
            if (cls === "principal") {
                const currentPrincipal = diagnoses.find(
                    (d) => d.classification === "principal" && d.icdCode !== pickerTarget.icdCode
                );
                if (currentPrincipal) {
                    onChangeClassification(currentPrincipal.icdCode, "other");
                    setToastText("Previous principal demoted to Other");
                    setTimeout(() => setToastText(null), 2500);
                }
            }
            onChangeClassification(pickerTarget.icdCode, cls);
            setPickerVisible(false);
            setPickerTarget(null);
        },
        [pickerTarget, diagnoses, onChangeClassification]
    );

    // ── Navigate from source row ──

    const handleNavigate = useCallback(
        (source: DiagnosisSource) => {
            animateOut(() => {
                if (source.type === "med") {
                    onNavigateToMedications();
                } else if (source.type === "section") {
                    onNavigateToSection(source.label);
                }
            });
        },
        [animateOut, onNavigateToSection, onNavigateToMedications]
    );

    // ── Summary stats ──

    const principalCount = diagnoses.filter((d) => d.classification === "principal").length;
    const sectionCount = new Set(
        diagnoses.flatMap((d) => d.sources.filter((s) => s.type === "section").map((s) => s.label))
    ).size;

    // ── Render ──

    return (
        <Modal
            visible={visible}
            transparent
            animationType="none"
            onShow={animateIn}
            onRequestClose={handleClose}
            statusBarTranslucent
        >
            {/* Backdrop */}
            <Animated.View style={[styles.backdrop, { opacity: backdropAnim }]}>
                <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
            </Animated.View>

            {/* Sheet */}
            <Animated.View style={[styles.sheet, { transform: [{ translateY: slideAnim }] }]}>
                {/* Drag handle */}
                <TouchableOpacity style={styles.handleArea} onPress={handleClose} activeOpacity={1}>
                    <View style={styles.handle} />
                </TouchableOpacity>

                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <Text style={styles.title}>Diagnosis Hub</Text>
                        <View style={styles.countBadge}>
                            <Text style={styles.countText}>{diagnoses.length} found</Text>
                        </View>
                    </View>
                    <View style={styles.summaryRow}>
                        <Text style={styles.summaryText}>{diagnoses.length} diagnoses</Text>
                        <View style={styles.summaryDot} />
                        <Text style={styles.summaryText}>{principalCount} principal</Text>
                        <View style={styles.summaryDot} />
                        <Text style={styles.summaryText}>{sectionCount} sections</Text>
                    </View>
                </View>

                {/* Diagnosis list or empty state */}
                {diagnoses.length === 0 ? (
                    <View style={styles.emptyState}>
                        <View style={styles.emptyIcon}>
                            <MaterialCommunityIcons
                                name="clipboard-text-outline"
                                size={48}
                                color={colors.teal400}
                            />
                        </View>
                        <Text style={styles.emptyTitle}>No diagnoses documented yet</Text>
                        <Text style={styles.emptySubtitle}>
                            Diagnoses will appear here as you complete clinical sections and review
                            medications.
                        </Text>
                    </View>
                ) : (
                    <ScrollView
                        style={styles.list}
                        contentContainerStyle={styles.listContent}
                        showsVerticalScrollIndicator={false}
                    >
                        {diagnoses.map((dx) => (
                            <DiagnosisCard
                                key={dx.icdCode}
                                diagnosis={dx}
                                onMenuPress={(source) => openMenu(dx, source)}
                                onClassificationPress={() => {
                                    setPickerTarget(dx);
                                    setPickerVisible(true);
                                }}
                                onNavigate={handleNavigate}
                            />
                        ))}
                    </ScrollView>
                )}
            </Animated.View>

            {/* Context Menu — inside the Modal, sibling of sheet, renders above it */}
            {menuVisible && menuTarget && (
                <Pressable style={styles.menuOverlay} onPress={closeMenu}>
                    <View style={styles.menuCard}>
                        <Text style={styles.menuCardTitle}>
                            {menuTarget.dx.icdCode} — {menuTarget.dx.description}
                        </Text>

                        <TouchableOpacity style={styles.menuItem} onPress={handleMenuNavigate}>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={18}
                                color={colors.textPrimary}
                            />
                            <Text style={styles.menuItemText}>
                                {menuTarget.source.type === "section"
                                    ? `Go to ${menuTarget.source.label}`
                                    : menuTarget.source.type === "med"
                                      ? "Go to Medications"
                                      : "Agency pre-visit"}
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity style={styles.menuItem} onPress={handleMenuClassify}>
                            <MaterialCommunityIcons
                                name="pencil-outline"
                                size={18}
                                color={colors.textPrimary}
                            />
                            <Text style={styles.menuItemText}>Change classification</Text>
                        </TouchableOpacity>

                        <View style={styles.menuDivider} />

                        {menuTarget.source.type === "med" ? (
                            <TouchableOpacity style={styles.menuItem} onPress={handleMenuNavigate}>
                                <MaterialCommunityIcons
                                    name="chevron-right"
                                    size={18}
                                    color={colors.gray400}
                                />
                                <Text style={[styles.menuItemText, { color: colors.gray400 }]}>
                                    Go to Medications to edit
                                </Text>
                            </TouchableOpacity>
                        ) : (
                            <TouchableOpacity style={styles.menuItem} onPress={handleMenuRemove}>
                                <MaterialCommunityIcons
                                    name="trash-can-outline"
                                    size={18}
                                    color={colors.error}
                                />
                                <Text style={[styles.menuItemText, { color: colors.error }]}>
                                    Remove diagnosis
                                </Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </Pressable>
            )}

            {/* Classification Picker — inside the Modal, sibling of sheet */}
            {pickerVisible && pickerTarget && (
                <Pressable
                    style={styles.menuOverlay}
                    onPress={() => {
                        setPickerVisible(false);
                        setPickerTarget(null);
                    }}
                >
                    <View style={styles.menuCard}>
                        <Text style={styles.menuCardTitle}>Change Classification</Text>
                        {(["principal", "surgical", "other"] as DiagnosisClassification[]).map(
                            (cls) => (
                                <TouchableOpacity
                                    key={cls}
                                    style={styles.pickerItem}
                                    onPress={() => handleClassificationChange(cls)}
                                >
                                    <View
                                        style={[
                                            styles.pickerDot,
                                            {
                                                backgroundColor:
                                                    cls === "principal"
                                                        ? colors.teal400
                                                        : cls === "surgical"
                                                          ? colors.pink400
                                                          : colors.gray400
                                            }
                                        ]}
                                    />
                                    <Text style={styles.pickerLabel}>
                                        {cls.charAt(0).toUpperCase() + cls.slice(1)}
                                    </Text>
                                    {pickerTarget.classification === cls && (
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={16}
                                            color={colors.teal600}
                                            style={styles.pickerCheck}
                                        />
                                    )}
                                </TouchableOpacity>
                            )
                        )}
                    </View>
                </Pressable>
            )}

            {/* Delete Confirmation — inline inside Modal */}
            {deleteTarget && (
                <Pressable style={styles.menuOverlay} onPress={() => setDeleteTarget(null)}>
                    <View style={styles.menuCard}>
                        <Text style={styles.deleteTitle}>Remove Diagnosis?</Text>
                        <Text style={styles.deleteMessage}>
                            This will remove{" "}
                            <Text style={{ fontFamily: fonts.bold }}>
                                {deleteTarget.dx.icdCode} — {deleteTarget.dx.description}
                            </Text>{" "}
                            from this visit.
                        </Text>
                        <View style={styles.deleteActions}>
                            <TouchableOpacity
                                style={styles.deleteBtnCancel}
                                onPress={() => setDeleteTarget(null)}
                            >
                                <Text style={styles.deleteBtnCancelText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.deleteBtnConfirm}
                                onPress={confirmDeleteAction}
                            >
                                <Text style={styles.deleteBtnConfirmText}>Remove</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Pressable>
            )}

            {/* Undo Snackbar */}
            <Snackbar
                visible={undoVisible}
                onDismiss={() => setUndoVisible(false)}
                duration={8000}
                action={{
                    label: "UNDO",
                    textColor: colors.teal400,
                    onPress: () => {
                        // Parent should re-add the diagnosis
                        setUndoVisible(false);
                        setUndoPending(null);
                    }
                }}
                style={styles.snackbar}
            >
                <Text style={styles.snackbarText}>Diagnosis removed</Text>
            </Snackbar>

            {/* Toast for classification change */}
            {toastText && (
                <View style={styles.toast}>
                    <MaterialCommunityIcons name="check-circle" size={16} color={colors.success} />
                    <Text style={styles.toastText}>{toastText}</Text>
                </View>
            )}
        </Modal>
    );
}

// ── Styles ──

const styles = StyleSheet.create({
    // Backdrop
    backdrop: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(0, 0, 0, 0.4)"
    },

    // Sheet
    sheet: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: SCREEN_HEIGHT * 0.92,
        backgroundColor: colors.surface,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        ...shadows.xl
    },

    // Handle
    handleArea: {
        paddingTop: 12,
        paddingBottom: 4,
        alignItems: "center"
    },
    handle: {
        width: 36,
        height: 5,
        borderRadius: 3,
        backgroundColor: colors.gray300
    },

    // Header
    header: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.md
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    title: {
        fontSize: 20,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    countBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 20
    },
    countText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.teal600
    },
    summaryRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 6
    },
    summaryText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    summaryDot: {
        width: 3,
        height: 3,
        borderRadius: 2,
        backgroundColor: colors.gray300
    },

    // List
    list: {
        flex: 1
    },
    listContent: {
        padding: spacing.base,
        paddingTop: spacing.xs,
        paddingBottom: spacing["3xl"]
    },

    // Empty state
    emptyState: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: spacing["3xl"]
    },
    emptyIcon: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: spacing.xl
    },
    emptyTitle: {
        fontSize: 17,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: spacing.sm
    },
    emptySubtitle: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textTertiary,
        textAlign: "center",
        lineHeight: 19
    },

    // Context menu overlay (full-screen, inside Modal, above sheet)
    menuOverlay: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 200,
        padding: 40
    },
    menuCard: {
        backgroundColor: colors.surface,
        borderRadius: 20,
        width: "100%",
        maxWidth: 320,
        padding: 16,
        ...shadows.xl
    },
    menuCardTitle: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary,
        marginBottom: 8,
        paddingHorizontal: 6
    },
    menuItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 10,
        paddingHorizontal: 14,
        borderRadius: radius.xs
    },
    menuItemText: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textPrimary
    },
    menuDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginVertical: 4
    },

    // Classification picker
    pickerItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 10,
        paddingHorizontal: 14,
        borderRadius: radius.xs
    },
    pickerDot: {
        width: 8,
        height: 8,
        borderRadius: 4
    },
    pickerLabel: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary,
        flex: 1
    },
    pickerCheck: {
        marginLeft: "auto"
    },

    // Delete confirmation
    deleteTitle: {
        fontSize: 18,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        marginBottom: 8
    },
    deleteMessage: {
        fontSize: 14,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        lineHeight: 20,
        marginBottom: 20
    },
    deleteActions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 8
    },
    deleteBtnCancel: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 12,
        backgroundColor: colors.gray100
    },
    deleteBtnCancelText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.textSecondary
    },
    deleteBtnConfirm: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 12,
        backgroundColor: colors.error50
    },
    deleteBtnConfirmText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.error
    },

    // Snackbar
    snackbar: {
        backgroundColor: colors.gray800,
        borderRadius: radius.md,
        marginHorizontal: spacing.lg
    },
    snackbarText: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: "#FFFFFF"
    },

    // Toast
    toast: {
        position: "absolute",
        top: 60,
        left: spacing.lg,
        right: spacing.lg,
        backgroundColor: colors.gray800,
        borderRadius: radius.md,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        ...shadows.lg,
        zIndex: 200
    },
    toastText: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: "#FFFFFF"
    }
});
